import type { MetaFunction } from "@remix-run/node"

import * as Flow from "@iyk/flow"
import * as UI from "@iyk/ui"
import * as React from "react"
import * as OpenGraph from "../../lib/utils/open-graph.ts"

import { DateTime } from "@iyk/date-time"
import { toSentenceCase, toUpperCase } from "@iyk/string"
import { Await, useLoaderData } from "@remix-run/react"
import {
  PointsOnboardingDialog,
  usePointsOnboarding,
} from "../../components/points/points-onboarding.tsx"
import { ItemMediaGallery } from "../../components/profiles/item-media-gallery.tsx"
import { ItemMedia } from "../../components/profiles/item-media.tsx"
import { ProfileLayout } from "../../components/profiles/layout.tsx"
import { OtherChippedItems } from "../../components/profiles/other-chipped-items.tsx"
import { AsyncOwnerHoverCard } from "../../components/profiles/owner-hover-card.tsx"
import { ShareDialog } from "../../components/profiles/share-dialog.tsx"
import { AsyncTeamLinkWithHoverCard } from "../../components/profiles/team-link-with-hover-card.tsx"
import { ReferrerDialog } from "../../components/referrals/referrer-dialog.tsx"
import { ProductPrice } from "../../components/shop/product-price.tsx"
import { ShopDialog } from "../../components/shop/shop-dialog.tsx"
import { useIykUser } from "../../lib/hooks/use-iyk-user.ts"
import { useLang } from "../../lib/lang/use-lang.ts"
import { CollapsibleSection } from "../../lib/ui/collapsible-section.tsx"
import { loader } from "./loader.server.ts"

export { loader }

export const meta: MetaFunction<typeof loader> = ({ data, location }) => {
  if (!data) return [{ title: "IYK" }]
  const title = `${data.mintedItem.name} | IYK`
  return [
    { title },
    ...OpenGraph.generateProperties({
      title,
      pathname: location.pathname,
      imageUrl: data.mintedItem.media?.[0]?.src,
      description: data.mintedItem.description,
      type: "website",
    }),
  ]
}

export default function MintedItemDetailPage() {
  const data = useLoaderData<typeof loader>()
  const lang = useLang()
  const iykUser = useIykUser()

  const avatarUrl = data.mintedItem.team.iconUrl || UI.toInitialsSVG(data.mintedItem.team.name[0])
  const productImage = data.mintedItem.media?.[0]?.src || UI.toInitialsSVG(data.mintedItem.name[0])

  const { showPointsOnboardingDialog } = usePointsOnboarding()

  return (
    <ProfileLayout>
      {Flow.when(
        showPointsOnboardingDialog && data.pointsConfiguration && iykUser.isSignedIn,
        () => (
          <PointsOnboardingDialog />
        ),
      )}
      <div className="grid grid-cols-1 md:grid-cols-12 gap-4 md:gap-10 mb-10">
        <ItemMediaGallery mediaGallery={data.mintedItem.media} />
        <div className="flex flex-col gap-6 md:py-6 md:col-span-5">
          <div className="flex flex-col gap-6">
            <div className="flex flex-col gap-2">
              <AsyncTeamLinkWithHoverCard
                team={data.mintedItem.team}
                teamOwnersCountPromise={data.teamOwnersCountPromise}
                currentUserOwnedItemsPromise={data.currentUserOwnedItemsPromise}
              />
              <div className="flex items-center justify-between">
                <span className="text-xl">{data.mintedItem.name}</span>
                <ShareDialog
                  avatarUrl={avatarUrl}
                  itemName={data.mintedItem.name}
                  creatorName={data.mintedItem.team.name}
                  ownerName={data.owner?.username}
                  ownerAvatarUrl={data.owner?.avatarUrl || undefined}
                  type="itemDetail"
                />
              </div>
            </div>
            {data.owner ? (
              <AsyncOwnerHoverCard owner={data.owner} ownerStatsPromise={data.ownerStatsPromise} />
            ) : (
              <div className="flex items-center gap-3 cursor-pointer select-none">
                <UI.Avatar className="size-7" />
                <div className="flex flex-col gap-0.5">
                  <p className="text-xs-upper">{toSentenceCase(lang.terms.OWNER)}</p>
                  <p className="text-sm">{lang.terms.NONE}</p>
                </div>
              </div>
            )}
            {data.shopifyProduct && data.referralModule?.referrerData ? (
              <ReferrerDialog
                referrerData={data.referralModule.referrerData}
                pointsValueMapping={data.pointsConfiguration?.valueMapping}
                shopifyProduct={data.shopifyProduct}
                item={{
                  team: data.mintedItem.team,
                  name: data.mintedItem.name,
                  image: productImage,
                }}
              />
            ) : (
              data.shopifyProduct && (
                <ShopDialog
                  shopifyProduct={data.shopifyProduct}
                  referral={data.referralModule?.referralData}
                  team={data.mintedItem.team}
                  trigger={({ defaultVariant }) => (
                    <button className="flex items-center justify-between gap-3 p-1.5 bshadow bshadow-gray-6">
                      <div className="flex items-center gap-3">
                        <ItemMedia src={defaultVariant?.image?.url} className="w-7" />
                        {Flow.when(defaultVariant, (variant) => (
                          <ProductPrice
                            size="sm"
                            price={variant.price}
                            compareAtPrice={variant.compareAtPrice}
                          />
                        ))}
                      </div>
                      <span className={UI.classForButton({ kind: "solid", size: "sm" })}>
                        {toSentenceCase(lang.terms.SHOP_THIS_STYLE)}
                      </span>
                    </button>
                  )}
                />
              )
            )}
          </div>

          {data.mintedItem.description !== "" && (
            <CollapsibleSection
              cutoff={400}
              key={data.mintedItem.id} // Make sure we re-render on route/item change - to re-calc min-height
            >
              <p className="text-balance whitespace-pre-line text-sm">
                {data.mintedItem.description}
              </p>
            </CollapsibleSection>
          )}

          {data.mintedItem.properties && data.mintedItem.properties.length > 0 && (
            <div className="flex flex-col gap-2">
              <p className="text-xs-upper">{toUpperCase("Attributes")}</p>
              <ul className="flex flex-col gap-1">
                {data.mintedItem.properties.map((property) => (
                  <li key={property.name} className="flex gap-1">
                    <p className="text-sm">— {property.name}:</p>
                    <p className="text-sm">{property.value}</p>
                  </li>
                ))}
              </ul>
            </div>
          )}

          <div className="flex gap-14">
            <div className="flex flex-col gap-1">
              <p className="text-xs-upper">{toSentenceCase(lang.terms.CREATED)}</p>
              <p className="text-sm">
                {DateTime.from(data.mintedItem.createdAt).toString({
                  dateStyle: "medium",
                })}
              </p>
            </div>
            {data.owner && (
              <div className="flex flex-col gap-1">
                <p className="text-xs-upper">{toSentenceCase(lang.terms.CLAIMED)}</p>
                <p className="text-sm">
                  {DateTime.from(data.owner.claimedAt).toString({
                    dateStyle: "medium",
                  })}
                </p>
              </div>
            )}
          </div>
          <UI.Link
            href={data.mintedItem.explorerUrl}
            rel="noreferrer"
            target="_blank"
            underline={false}
            className="w-fit"
          >
            {toSentenceCase(lang.terms.VIEW_ON_ETHERSCAN)}
            <UI.Icon.Etherscan />
          </UI.Link>
        </div>
      </div>

      <React.Suspense fallback={null}>
        <Await resolve={data.relatedItemsPromise}>
          {(relatedItems) => (
            <OtherChippedItems
              items={relatedItems}
              currentUserOwnedItemsPromise={data.currentUserOwnedItemsPromise}
            />
          )}
        </Await>
      </React.Suspense>
    </ProfileLayout>
  )
}
